import { useEffect, useRef, useState } from 'react'
import { Crosshair } from 'lucide-react'
import { Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { AsyncMultiSelect } from '@/shared/ui/filters'
import { listUnits } from '@/features/geography/api'
import { explain } from '@/shared/api/problem'
import type { ActivityTypeCode } from '@/shared/api/types'
import { recordActivity } from './api'
import { ACTIVITY, CAPTURABLE } from './vocab'

/**
 * Recording one thing that happened.
 *
 * ## The idempotency key is chosen once, before the first attempt
 *
 * A phone in a village with one bar retries. A key generated per *attempt* is no
 * key at all — the retry gets a new one and the leaderboard counts the work
 * twice. So the key is minted when the dialog opens and held in a ref: every
 * retry of the same capture carries the same key, and the server returns the
 * original row rather than creating a second.
 *
 * A new key is minted after a successful save, because the next thing the user
 * records is a different event.
 *
 * ## Location is optional and a bad fix is still recorded
 *
 * `CHECK_IN` with no coordinates is still a check-in. Rural GPS fails, and
 * refusing the write teaches people to stop checking in — which loses the whole
 * record, not just the coordinates. A 2,000-metre accuracy reading is data
 * *about* the reading and is stored as such; whether it is trustworthy is a
 * question for the quality check, not for this form.
 *
 * ## Two types are missing from the list on purpose
 *
 * `TRANSFER_RAISED` and `TRANSFER_RECORDED` are written automatically when a
 * transfer is raised elsewhere. Offering them here would let somebody type a
 * second row for one event, and every score built on the log would count it twice.
 */
export function CaptureDialog({
  open, defaultUnitId, defaultUnitName, onClose, onRecorded,
}: {
  open: boolean
  defaultUnitId: string | null
  defaultUnitName: string | null
  onClose: () => void
  onRecorded: () => void
}) {
  const [type, setType] = useState<ActivityTypeCode>('CHECK_IN')
  const [note, setNote] = useState('')
  const [unit, setUnit] = useState<{ value: string; label: string }[]>([])
  const [coords, setCoords] = useState<{ lat: number; lon: number; acc: number | null } | null>(null)
  const [locating, setLocating] = useState(false)
  const [locateNote, setLocateNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // Minted when the dialog opens, kept across every retry of THIS capture.
  const key = useRef<string>(crypto.randomUUID())

  useEffect(() => {
    if (!open) return
    key.current = crypto.randomUUID()
    setType('CHECK_IN')
    setNote('')
    setCoords(null)
    setLocateNote(null)
    setError(null)
    setSaved(false)
    setUnit(defaultUnitId
      ? [{ value: defaultUnitId, label: defaultUnitName ?? 'Your home unit' }]
      : [])
  }, [open, defaultUnitId, defaultUnitName])

  const locate = () => {
    if (!('geolocation' in navigator)) {
      setLocateNote('This device cannot report a location. The capture still works without one.')
      return
    }
    setLocating(true)
    setLocateNote(null)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          acc: Number.isFinite(pos.coords.accuracy) ? Math.round(pos.coords.accuracy) : null,
        })
        setLocating(false)
      },
      () => {
        // Not an error. Refusing the write here is how people learn to stop
        // recording anything at all.
        setLocateNote('Could not get a location. Recording without one is fine.')
        setLocating(false)
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30_000 },
    )
  }

  const save = async () => {
    setBusy(true); setError(null)
    try {
      await recordActivity({
        activityType: type,
        occurredAt: new Date().toISOString(),
        unitId: unit[0]?.value ?? null,
        latitude: coords?.lat ?? null,
        longitude: coords?.lon ?? null,
        accuracyMetres: coords?.acc ?? null,
        detail: note.trim() ? { note: note.trim() } : null,
      }, key.current)
      setSaved(true)
      onRecorded()
    } catch (err) {
      // The key is NOT rotated here. A failure may or may not have reached the
      // server; retrying with the same key is what makes that safe.
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Record something"
      description="Saved once, even if you tap twice or the connection drops mid-send."
      footer={saved ? (
        <button type="button" className="erp-btn-primary" onClick={onClose}>Done</button>
      ) : (
        <>
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="erp-btn-primary" disabled={busy}
                  onClick={() => void save()}>
            {busy ? 'Recording…' : 'Record'}
          </button>
        </>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />

        {saved ? (
          <Note tone="good">
            Recorded. It is in the feed now and cannot be edited — which is what makes it
            worth anything as evidence.
          </Note>
        ) : (
          <>
            <label className="block">
              <span className="block text-erp-xs text-muted-foreground mb-1">What happened</span>
              <select className="erp-input w-full" value={type}
                      onChange={(e) => setType(e.target.value as ActivityTypeCode)}>
                {CAPTURABLE.map((c) => (
                  <option key={c} value={c}>{ACTIVITY[c].label}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="block text-erp-xs text-muted-foreground mb-1">Where</span>
              <AsyncMultiSelect
                label="Unit"
                selected={unit}
                onChange={(next) => setUnit(next.slice(-1))}
                placeholder="Search a ward or station"
                search={async (term) => {
                  const found = await listUnits({ q: term } as never, 0, 10)
                  return found.content.map((u) => ({ value: u.id, label: u.name, sub: u.code }))
                }}
              />
            </label>

            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" className="erp-btn" disabled={locating} onClick={locate}>
                  <Crosshair className="h-3.5 w-3.5" />
                  {locating ? 'Locating…' : coords ? 'Update location' : 'Add my location'}
                </button>
                {coords && (
                  <span className="text-erp-xs text-muted-foreground font-mono">
                    {coords.lat.toFixed(5)}, {coords.lon.toFixed(5)}
                    {coords.acc !== null ? ` ±${coords.acc} m` : ''}
                  </span>
                )}
              </div>
              {locateNote && (
                <p className="text-erp-xs text-muted-foreground">{locateNote}</p>
              )}
              {coords && coords.acc !== null && coords.acc > 500 && (
                <p className="text-erp-xs text-muted-foreground">
                  That is a rough fix. It is recorded as-is — how precise it was is part of
                  the record, not a reason to throw it away.
                </p>
              )}
            </div>

            <label className="block">
              <span className="block text-erp-xs text-muted-foreground mb-1">
                Note (optional)
              </span>
              <textarea className="erp-input w-full min-h-20" value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Anything the coordinator should know" />
            </label>
          </>
        )}
      </div>
    </Modal>
  )
}
